import { useEffect, useState } from "react";
import ContainerPage from "../../components/Containers/ContainerPage";
import TitlePage from "../../components/Texts/TitlePage";
import {
  BUTTON_CREATE_USER,
  EXTRA_SEATS_ADDED,
  EXTRA_SEATS_CHANGE_SAVED,
  USERS_TITLE,
} from "../../i18n/keysTranslations";
import { useTranslationApp } from "../../lib/i18next";
import { startGetAdsUsers } from "../../actions/user";
import { useDispatchApp, useSelectorApp } from "../../lib/redux";
import { getPathDatabaseByRole } from "../../actions/auth";
import { useNavigate } from "react-router-dom";
import { useDefaultAppRoute } from "../../hooks/useIsAdvertisersDemo";
import UsersTable from "./components/UsersTable";
import Button from "../../components/Buttons/Button";
import Box from "@mui/material/Box";
import ModalUser from "./components/ModalUser";
import { SimpleAlert } from "../../components/Alerts/Alerts";
import ModalManageExtraSeats from "../../components/Modal/ModalManageExtraSeats";
import { useAgencySeats } from "../../hooks/useAgencyFeatureAddons";
import useSeatsCheckoutReturn from "../../hooks/useSeatsCheckoutReturn";
import {
  ADVERTISER_ROLE,
  ALERT_ICON_TYPE_SUCCESS,
  SUPER_ADMIN_ROLE,
} from "../../utils/constants";
import { Helmet } from "react-helmet";
import AddIcon from "@mui/icons-material/Add";
import useCheckAnonymous from "./hooks/useCheckAnonymous";
import { setShowModalSelectSubscription } from "../../actions/ui";
import {
  CREATE_USER_ACTION,
  SEAT_LIMIT_REACHED_CODE,
  SEATS_CHANGE_OUTCOME,
  SEATS_RETURN_TO,
  readSeatDraftUser,
  resolveCreateUserAction,
  resolveSeatLimitReachedAction,
  seatsStatusFromCheckout,
  shouldOpenUserFormAfterCheckout,
} from "../../utils/extraSeats";

const Users = ({ embedded = false }) => {
  const [isLoading, setIsLoading] = useState(true);
  const [modalUserOpen, setModalUserOpen] = useState(false);
  const [isPreparingSeat, setIsPreparingSeat] = useState(false);
  const [draftUser, setDraftUser] = useState(null);
  const [seatsPaywallOpen, setSeatsPaywallOpen] = useState(false);
  const [paywallStatus, setPaywallStatus] = useState(null);
  const { t } = useTranslationApp();
  const dispatch = useDispatchApp();
  const navigate = useNavigate();
  const defaultAppRoute = useDefaultAppRoute();
  const { agencyID, role } = dispatch(getPathDatabaseByRole());
  const currentUserId = useSelectorApp((state) => state.auth?.user?.uid);
  const { theme } = useSelectorApp((state) => state.ui);
  const users = useSelectorApp((state) => state.user.users);
  const planUsersFallback = useSelectorApp(
    (state) => state.payments?.subscriptionPlanInfo?.benefits?.users
  );
  const { checkIsAnonymous } = useCheckAnonymous();
  const { seatsStatus, refreshSeatsStatus, applySeatsStatus } =
    useAgencySeats();

  const getInitialData = async () => {
    if (!agencyID || (role !== ADVERTISER_ROLE && role !== SUPER_ADMIN_ROLE)) {
      navigate(defaultAppRoute);
    }
    await dispatch(
      startGetAdsUsers({
        onCallbackLoadingFinish: () => setIsLoading(false),
      })
    );
  };

  useEffect(() => {
    getInitialData();
    // eslint-disable-next-line
  }, []);

  const onUpgradePlan = () => {
    dispatch(setShowModalSelectSubscription(true));
  };

  const onBuySeat = ({ status, draft = null }) => {
    setDraftUser(draft);
    setPaywallStatus(status || null);
    setSeatsPaywallOpen(true);
  };

  const onAddUser = async () => {
    if (checkIsAnonymous()) return;
    setDraftUser(null);
    let status = seatsStatus;
    let action = resolveCreateUserAction({
      seatsStatus: status,
      users,
      planUsersFallback,
    });
    // While the seat status is still loading, the plan benefit may miss purchased seats.
    if (!status && action !== CREATE_USER_ACTION.FORM) {
      setIsPreparingSeat(true);
      status = await refreshSeatsStatus();
      setIsPreparingSeat(false);
      action = resolveCreateUserAction({
        seatsStatus: status,
        users,
        planUsersFallback,
      });
    }
    if (action === CREATE_USER_ACTION.BUY_SEATS) {
      onBuySeat({ status });
      return;
    }
    if (action === CREATE_USER_ACTION.UPGRADE_PLAN) {
      onUpgradePlan();
      return;
    }
    setModalUserOpen(true);
  };

  const onSeatLimitReached = ({ draftUser: nextDraft, seatLimit }) => {
    const status = seatLimit
      ? { ...(seatsStatus || {}), ...seatLimit }
      : seatsStatus;
    if (seatLimit) applySeatsStatus(status);
    const action = resolveSeatLimitReachedAction({
      ok: false,
      code: SEAT_LIMIT_REACHED_CODE,
      data: seatLimit,
    });
    if (action === CREATE_USER_ACTION.BUY_SEATS) {
      onBuySeat({ status, draft: nextDraft });
      return;
    }
    setDraftUser(nextDraft);
    onUpgradePlan();
  };

  useSeatsCheckoutReturn({
    onPaid: async (result) => {
      const draft = readSeatDraftUser();
      const status = seatsStatusFromCheckout(result?.data);
      if (status) applySeatsStatus(status);
      const canCreate = shouldOpenUserFormAfterCheckout({
        sessionStatus: result?.data?.sessionStatus,
        seatsStatus: status,
      });
      SimpleAlert({
        title: t(canCreate ? EXTRA_SEATS_ADDED : EXTRA_SEATS_CHANGE_SAVED),
        icon: ALERT_ICON_TYPE_SUCCESS,
      });
      if (canCreate) {
        setDraftUser(draft);
        setModalUserOpen(true);
      }
    },
  });

  const createUserButton = (
    <Button
      onClick={onAddUser}
      loading={isPreparingSeat}
      disabled={isPreparingSeat}
      color={embedded ? "secondary" : "primary"}
      sx={{
        minWidth: 100,
        width: "auto",
      }}
    >
      {t(BUTTON_CREATE_USER)}
      <AddIcon
        sx={{
          color: embedded
            ? "inherit"
            : `${theme.palette.primary.contrastText} !important`,
          ml: 0.5,
          width: 20,
          height: 20,
        }}
      />
    </Button>
  );

  const usersContent = (
    <>
      {embedded ? (
        <Box
          sx={{ display: "flex", justifyContent: "flex-end", width: "100%" }}
        >
          {createUserButton}
        </Box>
      ) : (
        <Box
          sx={{
            display: "flex",
            flexDirection: "row",
            alignItems: "center",
            justifyContent: "space-between",
            width: "100%",
            mt: 4.5,
            mb: 2,
          }}
        >
          <TitlePage>{t(USERS_TITLE)}</TitlePage>
          <span style={{ display: "inline-block" }}>{createUserButton}</span>
        </Box>
      )}
      <UsersTable
        data={users}
        loading={isLoading}
        currentUserId={currentUserId}
      />
      {modalUserOpen && (
        <ModalUser
          open={modalUserOpen}
          onCloseModal={() => setModalUserOpen(false)}
          mode="create"
          initialValues={draftUser}
          onSeatLimitReached={onSeatLimitReached}
        />
      )}
      <ModalManageExtraSeats
        modalOpen={seatsPaywallOpen}
        onCloseModal={() => {
          setSeatsPaywallOpen(false);
          setDraftUser(null);
        }}
        onCompleted={(interpreted) => {
          setSeatsPaywallOpen(false);
          if (interpreted?.outcome === SEATS_CHANGE_OUTCOME.APPLIED) {
            refreshSeatsStatus();
            setModalUserOpen(true);
            return;
          }
          setDraftUser(null);
        }}
        onQuantityChanged={refreshSeatsStatus}
        seatsStatus={paywallStatus || seatsStatus}
        planUsersFallback={planUsersFallback}
        purpose="add"
        draftUser={draftUser}
        returnTo={SEATS_RETURN_TO.USERS}
      />
    </>
  );

  if (embedded) {
    return (
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: 2,
          width: "100%",
        }}
      >
        {usersContent}
      </Box>
    );
  }

  return (
    <ContainerPage mode="padding" hasMarginBottom={true} sx={{ px: 5 }}>
      <Helmet>
        <title>{`${theme.nameBusiness} - Users`}</title>
      </Helmet>
      {usersContent}
    </ContainerPage>
  );
};

export default Users;
